export interface ICartModifier {
    readonly modifierId: number;
    readonly modifierGuid?: string;
    readonly name: string;
    readonly priceDelta: number;
    readonly quantity: number;
}

export interface ICartModifierData {
    modifierId: number;
    modifierGuid?: string;
    name: string;
    priceDelta?: number;
    quantity?: number;
}

/**
 * What one CART selection costs: `priceDelta` is per instance and `quantity` the
 * count ("Extra Bacon ×2" at $2 → $4). The one conversion from the cart's
 * vocabulary to the order's, whose `priceDelta` is the selection's total — the
 * cart's own line total, the server repricer and the cart→order build all use
 * it, so cart, preview and order cannot price the same line differently.
 */
export function cartModifierSelectionTotal(modifier: {
    readonly priceDelta?: number | null;
    readonly quantity?: number | null;
}): number {
    return (modifier.priceDelta ?? 0) * (modifier.quantity ?? 1);
}

export class CartModifier implements ICartModifier {
    readonly modifierId: number;
    readonly modifierGuid?: string;
    readonly name: string;
    readonly priceDelta: number;
    readonly quantity: number;

    constructor(data: ICartModifierData) {
        this.modifierId = data.modifierId;
        this.modifierGuid = data.modifierGuid;
        this.name = data.name;
        this.priceDelta = data.priceDelta ?? 0;
        this.quantity = data.quantity ?? 1;
    }

    get totalPriceDelta(): number {
        return cartModifierSelectionTotal(this);
    }

    toJSON(): ICartModifier {
        return {
            modifierId: this.modifierId,
            modifierGuid: this.modifierGuid,
            name: this.name,
            priceDelta: this.priceDelta,
            quantity: this.quantity
        };
    }

    static fromJSON(data: Record<string, unknown>): CartModifier {
        if (typeof data.modifierId !== 'number') {
            throw new Error("CartModifier.fromJSON: modifierId must be a number.");
        }
        if (typeof data.name !== 'string') {
            throw new Error("CartModifier.fromJSON: name must be a string.");
        }

        return new CartModifier({
            modifierId: data.modifierId,
            modifierGuid: data.modifierGuid as string | undefined,
            name: data.name,
            priceDelta: typeof data.priceDelta === 'number' ? data.priceDelta : 0,
            quantity: typeof data.quantity === 'number' ? data.quantity : 1
        });
    }
}
